import { Component, OnInit, AfterViewInit, inject, viewChild } from '@angular/core';
import { Project } from 'src/app/entities/project/project';
import { NgClass, NgStyle, CommonModule } from '@angular/common';
import { StoreService } from 'src/app/services/store-service/store.service';
import { catchError, Observable } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FiltersService, ProjectsFormValues } from 'src/app/services/filters-service/filters.service';
import { MatSelectModule } from '@angular/material/select';
import { Client } from 'src/app/entities/client/client';
import { User } from 'src/app/entities/user/user';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ProjectRepository } from 'src/app/services/remote-data-service/repositories/project-repository/project.repository';
import { Roles, UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { OperationType } from 'src/app/models/operation-types';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { ProjectMutationsComponent } from 'src/app/components/atoms/mutations/project-mutations/project-mutations.component';
import { EntityItemsAnnihilatorComponent } from 'src/app/components/atoms/mutations/entity-items-annihilator/entity-items-annihilator.component';
import { SelectListComponent } from '../../atoms/select-list/select-list.component';
import log from 'loglevel';
import { BaseComponent } from '../../atoms/base-component/base.component';


@Component({
  selector: 'app-projects-page',
  imports: [
    CommonModule,
    NgStyle,
    ReactiveFormsModule,
    NgClass,
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    SelectListComponent
  ],
  templateUrl: './projects-page.component.html',
  styleUrls: ['./projects-page.component.scss'],
})
export class ProjectsPageComponent extends BaseComponent implements OnInit, AfterViewInit {

  /**
   * Logger for ProjectsPage component.
   */
  protected override logger = log.getLogger('ProjectPage');

  // TODO: Add team column where teams are implemented.
  displayedColumns: string[] = ['name', 'client', 'timeframe', 'timeStatus', 'update', 'delete'];
  dataSource!: MatTableDataSource<Project>;

  sort = viewChild(MatSort);
  projects: Project[] = [];
  clients: Client[] = [];
  users: User[] = [];

  projects$: Observable<Project[]>;

  isManager = false;

  /**
   * Formats the time spent in seconds to hours with up to 2 decimals
   */
  formatTimeSpent(seconds: number): string {
    const hours = seconds / 3600;
    return hours.toFixed(2).replace(/\.?0+$/, '');
  }
  clients$: Observable<Client[]>;
  users$: Observable<User[]>;


  showArchived = false;
  showActive = true;


  /**
   * `ReactiveForms` form to manage all the `projects` inputs.
   */
  public form = new FormGroup({
    title: new FormControl('', Validators.required),
    clients: new FormControl<string[]>([], Validators.required),
    members: new FormControl<string[]>([], Validators.required),
    archived: new FormControl<boolean>(false, Validators.required),
    active: new FormControl<boolean>(true, Validators.required)
  });

  constructor(
    protected store: StoreService,
    protected filters: FiltersService,
    protected projectRep: ProjectRepository,
    protected userRep: UserRepository,
    protected snackbar: SnackbarService
  ) {
    super();

    this.projects$ = this.store.projects;
    this.clients$ = this.store.clients;
    this.users$ = this.store.users;

    this.projects$.subscribe((projectsRes) => {
      // Show project if:
      // - It's active and showActive is true, OR
      // - It's archived and showArchived is true
      this.projects = projectsRes.filter(project => {
        return (project.active && this.showActive) ||
          (project.archived && this.showArchived);
      });

      this.dataSource = new MatTableDataSource(this.projects);

      const sort = this.sort();

      if (sort) {
        this.dataSource.sort = sort;
      }
    });

    this.clients$.subscribe((clients) => {
      this.clients = clients;
      this.setOptionsFromClients(clients)
    });

    this.users$.subscribe((users) => {
      this.users = users;
      this.setOptionsFromMembers(users)
    });
  }


  ngAfterViewInit() {
    if (this.dataSource) {

      const sort = this.sort();

      if (sort) {
        this.dataSource.sort = sort;
      }
    }
  }


  ngOnInit(): void {
    this.filters.setUserFilters({
      limit: 99999
    })
    this.filters.setProjectsFilters({
      limit: 99999
    })
    this.filters.setClientsFilters({
      limit: 99999
    })
    this.filters.setTagsFilters({
      limit: 99999
    })


    // Restore saved form values if they exist
    if (this.filters.hasFormValues('projectsFilters')) {
      const savedValues = this.filters.getFormValues<ProjectsFormValues>('projectsFilters') || {};
      this.form.patchValue(savedValues);

      this.setSelectedOption(this.selectListClients(), null, this.form.getRawValue().clients);
      this.setSelectedOption(this.selectListMembers(), null, this.form.getRawValue().members);


      // Update filter flags based on saved values - use type assertion to avoid TS errors
      const typedValues = savedValues as Record<string, unknown>;
      this.showArchived = typedValues['archived'] === true;
      this.showActive = typedValues['active'] !== false; // Default to showing active projects
    } else {
      // Set default values if no saved values exist
      this.form.patchValue({
        active: true,
        archived: false
      });
    }

    this.form.valueChanges.subscribe((changes) => {
      // Save form values
      this.filters.saveFormValues('projectsFilters', changes);

      // Use type assertion for the changes object
      const typedChanges = changes as Record<string, unknown>;

      this.filters.setProjectsFilters({
        title: typedChanges['title'] as string || undefined,
        clients: typedChanges['clients'] as string[] || undefined,
        members: typedChanges['members'] as string[] || undefined,
      });

      // Safely update filter flags
      this.showArchived = typedChanges['archived'] === true;
      this.showActive = typedChanges['active'] === true;

      // Re-filter the projects when filter settings change
      this.updateProjectsFilter();
    });

    this.userRep.getCurrentUser().subscribe((user) => {
      this.isManager = user.roles.includes(Roles.chronos_manager)
    })
  }


  /**
   * Updates the filtered projects based on current filter settings
   */
  updateProjectsFilter(): void {
    // Get all projects from the store
    this.store.projects.subscribe((allProjects) => {
      // Apply filters for active/archived projects
      this.projects = allProjects.filter(project => {
        return (project.active && this.showActive) ||
          (project.archived && this.showArchived);
      });

      // Update the table data source
      this.dataSource = new MatTableDataSource(this.projects);

      const sort = this.sort();
      if (sort) {
        this.dataSource.sort = sort;
      }
    }).unsubscribe(); // Unsubscribe immediately to avoid memory leaks
  }


  deleteProject(project: Project) {

    this.projectRep.removeProject(project).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      const projects = this.projects.filter(e => e !== project);
      if (projects) {
        this.projects = projects;
        this.snackbar.showSuccess('Deleted proyect')
        this.dataSource = new MatTableDataSource(projects);
      }
    })
  }


  readonly dialog = inject(MatDialog);

  openCreateProjectDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(ProjectMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.CREATE
      }
    });
  }

  openUpdateProjectDialog(
    projectId: string,
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(ProjectMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.UPDATE,
        id: projectId
      }
    });
  }

  openItemAnnihilatorDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(EntityItemsAnnihilatorComponent<Project>, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        entities: this.projects,
      }
    });
  }
}
